import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Polyline, Polygon, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import clsx from 'clsx';
import { formatMGRS } from '../../lib/mgrs';
import type { Geometry, GeoShape, LatLon } from '../../types/domain';

const DEFAULT_CENTER: [number, number] = [23.8, 90.4];
const DEFAULT_RADIUS_M = 150; // §15 default
const ACCENT = '#00d4aa'; // command accent — default stroke/fill for new geometry
const DEFAULT_FILL_OPACITY = 0.15; // §15 style default (no dedicated control)

// Simple numbered pin (avoids leaflet's broken default-marker asset under bundlers).
// 26px visual pin — big enough to grab and drag with a finger.
function pin(label: string, color = '#00d4aa'): L.DivIcon {
  return L.divIcon({
    className: 'geometry-pin',
    html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
      background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5);
      display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);font:700 11px/1 monospace;color:#0a0e14;">${label}</span></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });
}

function num(v: string): number | null {
  const n = Number(v);
  return v.trim() === '' || Number.isNaN(n) ? null : n;
}

function MapClick({ onClick }: { onClick: (ll: LatLon) => void }) {
  useMapEvents({ click: (e) => onClick({ lat: e.latlng.lat, lon: e.latlng.lng }) });
  return null;
}

function MgrsReadout() {
  const [center, setCenter] = useState<LatLon>({ lat: DEFAULT_CENTER[0], lon: DEFAULT_CENTER[1] });
  useMapEvents({
    moveend: (e) => {
      const c = e.target.getCenter();
      setCenter({ lat: c.lat, lon: c.lng });
    },
  });
  return (
    <div className="absolute bottom-2 left-2 z-[1000] bg-command-bg/90 border border-command-border rounded px-2 py-1 text-[10px] font-tactical text-gray-300">
      {formatMGRS(center.lat, center.lon)}
    </div>
  );
}

export default function GeometryPicker({
  value,
  onChange,
  shape: shapeProp,
}: {
  value?: Geometry | null;
  // Emits `null` until the operator actually places geometry — never a fabricated default.
  onChange: (g: Geometry | null) => void;
  shape?: GeoShape;
}) {
  const [shape, setShape] = useState<GeoShape>(shapeProp || value?.shape || 'POINT');
  // POINT center is tracked as two nullable fields so an un-placed picker stays truly empty
  // (no default marker/circle, no fabricated coordinates saved).
  const [centerLat, setCenterLat] = useState<number | null>(value?.centerLat ?? null);
  const [centerLon, setCenterLon] = useState<number | null>(value?.centerLon ?? null);
  const [radius, setRadius] = useState<number>(value?.radiusMeters ?? DEFAULT_RADIUS_M);
  const [coords, setCoords] = useState<LatLon[]>(value?.coords ?? []);

  // Per-object style (§15/§16) — seeded from the edited geometry, emitted nested
  // under `geometry.style`. fillOpacity has no control; it round-trips at its
  // seeded/default value.
  const [strokeColor, setStrokeColor] = useState<string>(value?.style?.strokeColor ?? ACCENT);
  const [fillColor, setFillColor] = useState<string>(value?.style?.fillColor ?? ACCENT);
  const [dashed, setDashed] = useState<boolean>(value?.style?.dashed ?? false);
  const [weight, setWeight] = useState<number>(value?.style?.weight ?? 2);
  const [opacity, setOpacity] = useState<number>(value?.style?.opacity ?? 1);
  const [fillOpacity] = useState<number>(value?.style?.fillOpacity ?? DEFAULT_FILL_OPACITY);

  const hasCenter = centerLat != null && centerLon != null;

  // Keep in sync if the parent forces a shape (e.g. target type change).
  useEffect(() => {
    if (shapeProp && shapeProp !== shape) setShape(shapeProp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapeProp]);

  const maxPts = shape === 'LINE' ? 2 : shape === 'BOX' ? 4 : 0;

  // Emit the current geometry upward — but ONLY when it is actually complete. An incomplete
  // shape (no POINT center, or too few LINE/BOX coords) emits null so the form can require it.
  // Members that don't apply to the shape are OMITTED entirely (never null): the backend's
  // geometry schema rejects null members, so POINT carries no `coords` key and LINE/BOX carry
  // no center/radius keys.
  useEffect(() => {
    const style = { strokeColor, fillColor, dashed, weight, opacity, fillOpacity };
    if (shape === 'POINT') {
      onChange(
        hasCenter
          ? { shape, centerLat: centerLat!, centerLon: centerLon!, radiusMeters: radius, style }
          : null
      );
    } else {
      onChange(coords.length === maxPts ? { shape, coords, style } : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape, centerLat, centerLon, radius, coords, strokeColor, fillColor, dashed, weight, opacity, fillOpacity]);

  const handleMapClick = (ll: LatLon) => {
    if (shape === 'POINT') {
      setCenterLat(ll.lat);
      setCenterLon(ll.lon);
    } else {
      setCoords((c) => (c.length >= maxPts ? [...c.slice(1), ll] : [...c, ll]));
    }
  };

  const setCoord = (i: number, ll: Partial<LatLon>) =>
    setCoords((c) => c.map((p, idx) => (idx === i ? { ...p, ...ll } : p)));

  const polygonPts = useMemo(() => coords.map((c) => [c.lat, c.lon] as [number, number]), [coords]);
  const viewCenter: [number, number] = [centerLat ?? DEFAULT_CENTER[0], centerLon ?? DEFAULT_CENTER[1]];

  return (
    <div className="space-y-2">
      {/* Shape selector */}
      <div className="flex gap-1">
        {(['POINT', 'LINE', 'BOX'] as GeoShape[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setShape(s);
              // Switching shape discards the other shape's data — operator re-places for the new shape.
              setCoords([]);
              if (s !== 'POINT') {
                setCenterLat(null);
                setCenterLon(null);
              }
            }}
            className={clsx(
              'px-3 sm:px-2 py-1 min-h-11 sm:min-h-0 rounded text-[11px] font-semibold border transition-colors touch-manipulation',
              shape === s
                ? 'bg-command-accent/20 text-command-accent border-command-accent/40'
                : 'text-gray-400 border-command-border hover:border-command-accent/40'
            )}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Style controls — stroke/fill swatches, solid/dotted, thickness, opacity.
          Emitted nested under geometry.style and mirrored live in the preview. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded border border-command-border bg-command-bg/60 px-2 py-1.5">
        <span className="text-[10px] font-tactical text-gray-500 uppercase tracking-wide">Style</span>

        <label className="flex items-center gap-1 text-[10px] text-gray-400">
          Stroke
          <input
            type="color"
            aria-label="Stroke colour"
            value={strokeColor}
            onChange={(e) => setStrokeColor(e.target.value)}
            className="h-6 w-6 cursor-pointer rounded border border-command-border bg-transparent p-0"
          />
        </label>

        <label className="flex items-center gap-1 text-[10px] text-gray-400">
          Fill
          <input
            type="color"
            aria-label="Fill colour"
            value={fillColor}
            onChange={(e) => setFillColor(e.target.value)}
            className="h-6 w-6 cursor-pointer rounded border border-command-border bg-transparent p-0"
          />
        </label>

        <button
          type="button"
          aria-label={dashed ? 'Line style: dotted' : 'Line style: solid'}
          aria-pressed={dashed}
          onClick={() => setDashed((d) => !d)}
          className={clsx(
            'px-2 py-1 min-h-8 rounded text-[10px] font-semibold border transition-colors touch-manipulation',
            dashed
              ? 'bg-command-accent/20 text-command-accent border-command-accent/40'
              : 'text-gray-400 border-command-border hover:border-command-accent/40'
          )}
        >
          {dashed ? 'DOTTED' : 'SOLID'}
        </button>

        <div className="flex items-center gap-1 text-[10px] text-gray-400">
          Weight
          <div className="flex items-center rounded border border-command-border overflow-hidden">
            <button
              type="button"
              aria-label="Decrease thickness"
              onClick={() => setWeight((w) => Math.max(1, w - 1))}
              className="px-2 py-0.5 min-h-8 text-gray-300 hover:bg-command-border/40 touch-manipulation"
            >
              −
            </button>
            <span className="px-1.5 tabular-nums text-gray-200" aria-label="Stroke thickness">
              {weight}
            </span>
            <button
              type="button"
              aria-label="Increase thickness"
              onClick={() => setWeight((w) => Math.min(8, w + 1))}
              className="px-2 py-0.5 min-h-8 text-gray-300 hover:bg-command-border/40 touch-manipulation"
            >
              +
            </button>
          </div>
        </div>

        <label className="flex items-center gap-1 text-[10px] text-gray-400">
          Opacity
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={opacity}
            onChange={(e) => setOpacity(Number(e.target.value))}
            aria-label="Stroke opacity"
            className="w-16 accent-command-accent"
          />
          <span className="tabular-nums w-8 text-right text-gray-300">{opacity.toFixed(2)}</span>
        </label>
      </div>

      {/* Typed fields */}
      {shape === 'POINT' ? (
        <div className="grid grid-cols-3 gap-2">
          <label className="text-[10px] text-gray-400">
            Lat
            <input
              aria-label="Center latitude"
              type="number"
              step="any"
              placeholder="—"
              value={centerLat ?? ''}
              onChange={(e) => setCenterLat(num(e.target.value))}
              className="w-full mt-0.5 px-2 py-1 min-h-11 sm:min-h-0 rounded bg-command-bg border border-command-border text-xs text-gray-100"
            />
          </label>
          <label className="text-[10px] text-gray-400">
            Lon
            <input
              aria-label="Center longitude"
              type="number"
              step="any"
              placeholder="—"
              value={centerLon ?? ''}
              onChange={(e) => setCenterLon(num(e.target.value))}
              className="w-full mt-0.5 px-2 py-1 min-h-11 sm:min-h-0 rounded bg-command-bg border border-command-border text-xs text-gray-100"
            />
          </label>
          <label className="text-[10px] text-gray-400">
            Radius (m)
            <input
              aria-label="Radius in metres"
              type="number"
              value={radius}
              onChange={(e) => setRadius(num(e.target.value) ?? radius)}
              className="w-full mt-0.5 px-2 py-1 min-h-11 sm:min-h-0 rounded bg-command-bg border border-command-border text-xs text-gray-100"
            />
          </label>
        </div>
      ) : (
        <div className="space-y-1">
          {Array.from({ length: maxPts }).map((_, i) => {
            const p = coords[i];
            return (
              <div key={i} className="grid grid-cols-2 gap-2 items-center">
                <label className="text-[10px] text-gray-400">
                  Pt {i + 1} lat
                  <input
                    aria-label={`Point ${i + 1} latitude`}
                    type="number"
                    step="any"
                    value={p?.lat ?? ''}
                    onChange={(e) => {
                      const v = num(e.target.value);
                      if (v == null) return;
                      if (!p) setCoords((c) => [...c, { lat: v, lon: 0 }]);
                      else setCoord(i, { lat: v });
                    }}
                    className="w-full mt-0.5 px-2 py-1 min-h-11 sm:min-h-0 rounded bg-command-bg border border-command-border text-xs text-gray-100"
                  />
                </label>
                <label className="text-[10px] text-gray-400">
                  Pt {i + 1} lon
                  <input
                    aria-label={`Point ${i + 1} longitude`}
                    type="number"
                    step="any"
                    value={p?.lon ?? ''}
                    onChange={(e) => {
                      const v = num(e.target.value);
                      if (v == null) return;
                      if (!p) setCoords((c) => [...c, { lat: 0, lon: v }]);
                      else setCoord(i, { lon: v });
                    }}
                    className="w-full mt-0.5 px-2 py-1 min-h-11 sm:min-h-0 rounded bg-command-bg border border-command-border text-xs text-gray-100"
                  />
                </label>
              </div>
            );
          })}
          <p className="text-[10px] text-gray-500">
            Click the map to drop the next point ({coords.length}/{maxPts}); drag pins to adjust.
          </p>
        </div>
      )}

      {shape === 'POINT' && !hasCenter && (
        <p className="text-[10px] text-amber-400/80">Click the map or type a lat/lon to place this location.</p>
      )}

      {/* Map */}
      <div className="relative h-72 rounded-lg overflow-hidden border border-command-border">
        <MapContainer center={viewCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
            attribution="&copy; Google"
          />
          <MapClick onClick={handleMapClick} />
          <MgrsReadout />

          {shape === 'POINT' && hasCenter && (
            <>
              <Circle
                center={[centerLat!, centerLon!]}
                radius={radius}
                pathOptions={{ color: strokeColor, fillColor, weight, opacity, fillOpacity, dashArray: dashed ? '6 6' : undefined }}
              />
              <Marker
                position={[centerLat!, centerLon!]}
                draggable
                icon={pin('C', strokeColor)}
                eventHandlers={{
                  dragend: (e) => {
                    const ll = (e.target as L.Marker).getLatLng();
                    setCenterLat(ll.lat);
                    setCenterLon(ll.lng);
                  },
                }}
              />
            </>
          )}

          {shape === 'LINE' && coords.length >= 2 && (
            <Polyline positions={polygonPts} pathOptions={{ color: strokeColor, weight, opacity, dashArray: dashed ? '6 6' : undefined }} />
          )}
          {shape === 'BOX' && coords.length >= 3 && (
            <Polygon positions={polygonPts} pathOptions={{ color: strokeColor, fillColor, weight, opacity, fillOpacity, dashArray: dashed ? '6 6' : undefined }} />
          )}
          {shape !== 'POINT' &&
            coords.map((c, i) => (
              <Marker
                key={i}
                position={[c.lat, c.lon]}
                draggable
                icon={pin(String(i + 1), strokeColor)}
                eventHandlers={{
                  dragend: (e) => {
                    const ll = (e.target as L.Marker).getLatLng();
                    setCoord(i, { lat: ll.lat, lon: ll.lng });
                  },
                }}
              />
            ))}
        </MapContainer>
      </div>
    </div>
  );
}
